/** ------------------------------------------------------------------------- *
 * Toggle Buttons *
 * -------------------------------------------------------------------------- *
 * @module Toggle
 * @desc
 * Toggles the display of a target element when clicking on a trigger element.
 * Settings and initial state are controlled by the attributes on the trigger
 * element and the state classes on the trigger and target elements.
 *
 * -------------------------- *
 * Trigger Element Attributes *
 * -------------------------- *
 * class="js-toggle"
 * The selector for initializing the class
 *
 * class="is-toggled"
 * The state class used for styling purposes (added when toggled)
 *
 * aria-controls="js-targetId"
 * The ID of the target element to be toggled
 *
 * aria-expanded="false"
 * The default state of the toggle elements
 *
 * data-group="navDropdown"
 * Optional: prevents multiple toggles from the same group being open
 *
 * role="button"
 * Optional: add for non-button elements
 *
 * tabindex="0"
 * Optional: add for elements that cannot normally receive focus
 *
 * ------------------------- *
 * Target Element Attributes *
 * ------------------------- *
 * id="js-targetId"
 * The ID matching the aria-controls attribute on the trigger element
 *
 * class="is-toggled"
 * The state class used for styling purposes (added when toggled)
 */

const settings = {
    // The default DOMString selector for initializing the module
    selector: '.js-toggle',
    // The class to be added to toggled elements
    state_class: 'is-toggled',
};

const group_arr = [];

/** ------------------ *
 * Toggle Button Class *
 * ------------------- *
 * @class ToggleButton
 * @desc Creates a clickable element that toggles the display of another element
 */

class ToggleButton {
    /**
     * @constructor
     * @param {Element} el - The HTML element that will trigger the toggle
     */

    constructor(el) {
        // Use attribute values for initial state
        const aria_controls = el.getAttribute('aria-controls');
        const aria_expanded = el.getAttribute('aria-expanded');
        const button_group = el.getAttribute('data-group');

        // Object properties
        this.trigger_el = el;
        this.target_el = document.getElementById(aria_controls);
        this.toggled = aria_expanded === 'true';

        // Add object to group array
        if (button_group !== undefined) {
            this.group = button_group;
            group_arr[this.group] = group_arr[this.group] || [];
            group_arr[this.group].push(this);
        }

        // Show/hide content on click
        this.trigger_el.addEventListener('click', (e) => {
            e.preventDefault();
            this.toggleDisplay();
        });
    }

    /**
     * @property ToggleButton.toggleDisplay
     * @method
     * @desc Toggles the element between shown and hidden
     */

    toggleDisplay() {
        // Update state
        function updateState(button) {
            button.trigger_el.classList.toggle(settings.state_class);
            button.trigger_el.setAttribute('aria-expanded', !button.toggled);
            button.target_el.classList.toggle(settings.state_class);
            button.toggled = !button.toggled;
        }

        // Check for button group
        if (this.group) {
            // Close open buttons in the same group
            group_arr[this.group].forEach((button) => {
                if (button !== this && button.toggled === true) {
                    updateState(button);
                }
            });
        }

        updateState(this);
    }
}

/** ------------ *
 * Module Export *
 * ------------- *
 */

const Toggle = {
    /**
     * @property Toggle.selector
     * @desc The default DOMString selector for initializing the module
     */

    selector: settings.selector,

    /**
     * @property Toggle.init
     * @method
     * @param {String} [selector] - The DOMString selector for the toggle button(s)
     * @desc Intializes the module
     */

    init(selector = settings.selector) {
        // Convert NodeList to Array
        const buttons = document.querySelectorAll(selector);
        const button_arr = Array.from(buttons);

        // Map the new Class object to the element
        button_arr.forEach((el) => {
            new ToggleButton(el);
        });
    },
};

export default Toggle;
